import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";

import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import AppDialog from "../../General/Dialog/AppDialog";

import GlassCard from "./GlassCard";
import InfoRow from "./InfoRow";

const formatJusticeAddress = (addr) => {
  const type = addr?.tipoDomicilio || "Sin tipo";
  const address = addr?.direccion || "—";
  const city = addr?.localidad ? `, ${addr.localidad}` : "";
  return `${type}: ${address}${city}`;
};

const formatManualAddress = (addr) => {
  const type = addr?.type || "Sin tipo";
  const address = addr?.address || "—";
  const city = addr?.city ? `, ${addr.city}` : "";
  return `${type}: ${address}${city}`;
};

const getJusticeAddressesCount = (party) => party?.addresses?.length || 0;
const getManualAddressesCount = (party) =>
  party?.notification_certificate_addresses?.length || 0;

function PartyAddressesDialog({ open, onClose, party, isMobile, isDark }) {
  const justiceAddresses = party?.addresses || [];
  const manualAddresses = party?.notification_certificate_addresses || [];

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Domicilios del demandado"
      subtitle={
        party
          ? `${`${party.name || ""} ${party.lastname || ""}`.trim()} · ${party.document || "Sin documento"}`
          : ""
      }
      maxWidth="md"
      fullScreenMobile
      fullScreenBreakpoint="md"
      stickyHeader
      contentSx={{
        p: { xs: 1.2, sm: 2 },
        background: "transparent",
      }}
      paperSx={{ borderRadius: isMobile ? 0 : 4, overflow: "hidden" }}
      actions={
        !isMobile ? (
          <Button onClick={onClose} sx={{ borderRadius: 2, fontWeight: 800 }}>
            Cerrar
          </Button>
        ) : null
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <GlassCard sx={{ p: 1.4 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              size="small"
              label={`${justiceAddresses.length} domicilios de justicia`}
              sx={{
                borderRadius: 2,
                fontWeight: 900,
                bgcolor: "rgba(25,118,210,0.10)",
                color: "primary.main",
              }}
            />
            <Chip
              size="small"
              label={`${manualAddresses.length} domicilios agregados`}
              sx={{
                borderRadius: 2,
                fontWeight: 900,
                bgcolor: "rgba(46,125,50,0.10)",
                color: "success.main",
              }}
            />
          </Box>
        </GlassCard>

        <GlassCard>
          <Typography sx={{ fontWeight: 900, mb: 1.2 }}>
            Domicilios de justicia
          </Typography>

          {justiceAddresses.length > 0 ? (
            <Box sx={{ display: "grid", gap: 1 }}>
              {justiceAddresses.map((addr, i) => (
                <Box
                  key={`justice-${i}`}
                  sx={{
                    p: 1.2,
                    borderRadius: 2.5,
                    border: isDark
                      ? "1px solid rgba(255,255,255,0.08)"
                      : "1px solid rgba(0,0,0,0.08)",
                    background: isDark
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(255,255,255,0.72)",
                  }}
                >
                  <Chip
                    size="small"
                    label="JUSTICIA"
                    sx={{
                      mb: 0.8,
                      borderRadius: 2,
                      fontWeight: 900,
                      bgcolor: "rgba(25,118,210,0.10)",
                      color: "primary.main",
                    }}
                  />

                  <Typography
                    sx={{
                      fontWeight: 850,
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {formatJusticeAddress(addr)}
                  </Typography>

                  {addr.fechaDesde ? (
                    <Typography variant="caption" sx={{ opacity: 0.72 }}>
                      Desde:{" "}
                      {new Date(addr.fechaDesde).toLocaleDateString("es-AR")}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Sin domicilios de justicia.
            </Typography>
          )}
        </GlassCard>

        <GlassCard>
          <Typography sx={{ fontWeight: 900, mb: 1.2 }}>
            Domicilios agregados
          </Typography>

          {manualAddresses.length > 0 ? (
            <Box sx={{ display: "grid", gap: 1 }}>
              {manualAddresses.map((addr, i) => (
                <Box
                  key={`manual-${i}`}
                  sx={{
                    p: 1.2,
                    borderRadius: 2.5,
                    border: addr.is_selected
                      ? "1px solid rgba(46,125,50,0.24)"
                      : "1px solid rgba(25,118,210,0.16)",
                    background: addr.is_selected
                      ? "rgba(46,125,50,0.06)"
                      : "rgba(25,118,210,0.06)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.8,
                      flexWrap: "wrap",
                      mb: 0.8,
                    }}
                  >
                    <Chip
                      size="small"
                      label="AGREGADO"
                      sx={{
                        borderRadius: 2,
                        fontWeight: 900,
                        bgcolor: "rgba(25,118,210,0.10)",
                        color: "primary.main",
                      }}
                    />

                    {addr.is_manual ? (
                      <Chip
                        size="small"
                        label="MANUAL"
                        sx={{
                          borderRadius: 2,
                          fontWeight: 900,
                          bgcolor: "rgba(251,140,0,0.10)",
                          color: "#c77700",
                        }}
                      />
                    ) : null}

                    {addr.is_selected ? (
                      <Chip
                        size="small"
                        label="SELECCIONADO"
                        sx={{
                          borderRadius: 2,
                          fontWeight: 900,
                          bgcolor: "rgba(46,125,50,0.10)",
                          color: "success.main",
                        }}
                      />
                    ) : null}
                  </Box>

                  <Typography
                    sx={{
                      fontWeight: 850,
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {formatManualAddress(addr)}
                  </Typography>

                  {addr.from_date ? (
                    <Typography variant="caption" sx={{ opacity: 0.72 }}>
                      Desde:{" "}
                      {new Date(addr.from_date).toLocaleDateString("es-AR")}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Sin domicilios agregados.
            </Typography>
          )}
        </GlassCard>
      </Box>
    </AppDialog>
  );
}

export default function TabPartes({ parties }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = theme.palette.mode === "dark";

  const [openAddressesDialog, setOpenAddressesDialog] = useState(false);
  const [selectedPartyAddresses, setSelectedPartyAddresses] = useState(null);

  const handleOpenAddressesDialog = (party) => {
    setSelectedPartyAddresses(party);
    setOpenAddressesDialog(true);
  };

  const handleCloseAddressesDialog = () => {
    setOpenAddressesDialog(false);
    setSelectedPartyAddresses(null);
  };

  return (
    <>
      {!parties.length ? (
        <GlassCard>
          <Typography sx={{ fontWeight: 950 }}>
            No hay partes registradas
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Cuando el expediente tenga partes, van a aparecer acá.
          </Typography>
        </GlassCard>
      ) : (
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}
        >
          {parties.map((party) => {
            const isHuman = party.type_person === "F";
            return (
              <GlassCard
                key={party.id}
                sx={{ p: { xs: 1.4, sm: 2 } }}
                right={
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleOpenAddressesDialog(party)}
                    sx={{
                      borderRadius: 2.2,
                      fontWeight: 900,
                      textTransform: "none",
                    }}
                  >
                    Ver domicilios
                  </Button>
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.2,
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: 3,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: isHuman
                        ? "rgba(25,118,210,0.12)"
                        : "rgba(0,0,0,0.06)",
                      color: isHuman
                        ? "primary.main"
                        : "text.primary",
                      border: "1px solid rgba(0,0,0,0.08)",
                      flexShrink: 0,
                    }}
                  >
                    {isHuman ? (
                      <PersonIcon sx={{ fontSize: 24 }} />
                    ) : (
                      <BusinessIcon sx={{ fontSize: 24 }} />
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 950 }}>
                      {isHuman
                        ? "Persona Física"
                        : "Persona Jurídica"}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                      Rol: {party.role || "—"}
                    </Typography>

                    <Divider sx={{ opacity: 0.55, my: 1.2 }} />

                    <Grid container spacing={1.2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <InfoRow
                          label="Nombre"
                          value={`${party.name || ""} ${party.lastname || ""}`.trim()}
                        />
                        <InfoRow
                          label="Documento"
                          value={party.document || "—"}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box
                          sx={{
                            p: 1.2,
                            borderRadius: 3,
                            border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                            background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.62)",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.7, fontWeight: 800 }}
                          >
                            Domicilios
                          </Typography>

                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              gap: 0.8,
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip
                              size="small"
                              label={`${getJusticeAddressesCount(party)} justicia`}
                              sx={{
                                borderRadius: 2,
                                fontWeight: 900,
                                bgcolor: "rgba(25,118,210,0.10)",
                                color: "primary.main",
                              }}
                            />

                            <Chip
                              size="small"
                              label={`${getManualAddressesCount(party)} agregados`}
                              sx={{
                                borderRadius: 2,
                                fontWeight: 900,
                                bgcolor: "rgba(46,125,50,0.10)",
                                color: "success.main",
                              }}
                            />
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              mt: 1,
                              opacity: 0.75,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {getManualAddressesCount(party) > 0
                              ? "Este demandado tiene domicilios agregados para cédulas."
                              : getJusticeAddressesCount(party) > 0
                                ? "Este demandado tiene domicilios provenientes de justicia."
                                : "No hay domicilios registrados."}
                          </Typography>

                          {(getJusticeAddressesCount(party) > 0 ||
                            getManualAddressesCount(party) > 0) && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() =>
                                handleOpenAddressesDialog(party)
                              }
                              sx={{
                                mt: 1.2,
                                borderRadius: 2.2,
                                fontWeight: 900,
                                textTransform: "none",
                              }}
                            >
                              Ver domicilios
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </GlassCard>
            );
          })}
        </Box>
      )}

      <PartyAddressesDialog
        open={openAddressesDialog}
        onClose={handleCloseAddressesDialog}
        party={selectedPartyAddresses}
        isMobile={isMobile}
        isDark={isDark}
      />
    </>
  );
}
